import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useLanguage } from '../../contexts/LanguageContext';
import { getServiceById, saveContact } from '../../services/storage';
import type { Service } from '../../types/api.types';
import type { Language } from '../../types/lang.types';
import { useSiteContent, tr } from '../../hooks/useSiteContent';
import SEOHead from '../../components/SEOHead';
import { metaDescription, HIGH_PRIORITY } from '../../utils/seo';
import { SERVICES_KEY, SERVICES_DEFAULTS, SERVICE_EXTRAS_KEY, SERVICE_EXTRAS_DEFAULTS, serviceImage, serviceFeatures } from '../../content/services';
import Img from '../../components/Img';

const MotionBox = motion(Box);


interface AppointmentForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  preferred_date: string;
  message: string;
}
const EMPTY_FORM: AppointmentForm = { name: '', email: '', phone: '', company: '', preferred_date: '', message: '' };

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentLanguage, t } = useLanguage();
  const content = useSiteContent(SERVICES_KEY, SERVICES_DEFAULTS);
  const extras = useSiteContent(SERVICE_EXTRAS_KEY, SERVICE_EXTRAS_DEFAULTS);
  const c = content.texts;
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<AppointmentForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    getServiceById(id ?? '').then((s) => { setService(s); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const titleKey = `title_${currentLanguage}` as `title_${Language}`;
  const descKey  = `description_${currentLanguage}` as `description_${Language}`;
  const lang     = currentLanguage as 'de' | 'en' | 'ar';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      await saveContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: `[Terminanfrage – ${service?.[titleKey]}]\nWunschdatum: ${form.preferred_date}\n\n${form.message}`,
      });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch {
      setSubmitError(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
        <CircularProgress size={36} thickness={2} sx={{ color: '#111' }} />
      </Box>
    );
  }

  if (!service) {
    return (
      <Container maxWidth="lg" sx={{ py: 16, textAlign: 'center' }}>
        <SEOHead title="Leistung nicht gefunden | TOP BHS" noindex />
        <Typography variant="h3" sx={{ mb: 3 }}>Service nicht gefunden</Typography>
        <Button component={RouterLink} to="/services" startIcon={<ArrowBackIcon />}>Zurück</Button>
      </Container>
    );
  }

  const imgSrc = serviceImage(service, content, extras);

  return (
    <Box>
      <SEOHead
        title={t('seo.service_title', { service: service[titleKey] })}
        description={metaDescription(service[descKey])}
      />
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 60%)',
          borderBottom: '1px solid #e8e8e8',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Button
            component={RouterLink}
            to="/services"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ color: 'text.secondary', mb: 5, px: 0, fontWeight: 400, '&:hover': { bgcolor: 'transparent', color: '#111', textDecoration: 'none' } }}
          >
            {t('services.title')}
          </Button>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <MotionBox initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}
                >
                  TOP BHS
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.8rem', sm: '3.5rem', md: '4.5rem' },
                    fontWeight: 800,
                    letterSpacing: '-2px',
                    lineHeight: 1.05,
                    mb: 4,
                    color: '#3d3d3d',
                  }}
                >
                  {service[titleKey]}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '1.15rem', lineHeight: 1.85, color: '#555', maxWidth: 520, mb: 5 }}
                >
                  {service[descKey]}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    href="#appointment"
                    endIcon={<CalendarMonthIcon />}
                    sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
                  >
                    {tr(c.book_appointment, lang)}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
                  >
                    {t('contact.title')}
                  </Button>
                </Box>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={5}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                <Img
                  src={imgSrc}
                  alt={service[titleKey]}
                  width={1200}
                  height={800}
                  {...HIGH_PRIORITY}
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: { xs: 260, md: 400 },
                    objectFit: 'cover',
                    borderRadius: 3,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.14)',
                  }}
                />
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── FEATURES / WHAT WE OFFER ─────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            sx={{ mb: 8 }}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
              {tr(c.included_label, lang)}
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' } }}>
              {tr(c.included_title, lang)}
            </Typography>
          </MotionBox>

          {(() => {
            const displayFeatures = serviceFeatures(service, content)[lang];
            return displayFeatures.length > 0 ? (
            <Grid container spacing={3}>
              {displayFeatures.map((feature: string, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={feature}>
                  <MotionBox
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: i * 0.07 }}
                    viewport={{ once: true }}
                    sx={{
                      p: 3.5,
                      border: '1px solid #e8e8e8',
                      borderRadius: 2,
                      height: '100%',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      '&:hover': { borderColor: '#111', boxShadow: '0 4px 20px rgba(0,0,0,0.07)' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#3d3d3d',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {feature}
                    </Typography>
                  </MotionBox>
                </Grid>
              ))}
            </Grid>
            ) : null;
          })()}
        </Container>
      </Box>

      {/* ── PROCESS ──────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#f8f8f8', py: { xs: 10, md: 14 }, borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8' }}>
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            sx={{ mb: 8 }}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
              {tr(c.process_label, lang)}
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' } }}>
              {tr(c.process_title, lang)}
            </Typography>
          </MotionBox>

          <Grid container spacing={4}>
            {(content.process[lang] ?? []).map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={step}>
                <MotionBox
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Typography
                    sx={{
                      fontSize: '3.5rem',
                      fontWeight: 800,
                      color: '#e8e8e8',
                      lineHeight: 1,
                      mb: 1.5,
                      letterSpacing: '-2px',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1rem' }}>
                    {step}
                  </Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── WHY US + APPOINTMENT FORM ─────────────────────────────────────── */}
      <Box id="appointment" sx={{ bgcolor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            {/* Why Us */}
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
                  TOP BHS
                </Typography>
                <Typography variant="h2" sx={{ mb: 5, fontSize: { xs: '2rem', md: '2.75rem' } }}>
                  {tr(c.why_title, lang)}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {(content.why_us[lang] ?? []).map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#111', fontSize: 22, flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#222' }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 5 }} />

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {tr(c.why_text, lang)}
                </Typography>
              </MotionBox>
            </Grid>

            {/* Appointment Form */}
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                sx={{
                  bgcolor: '#3d3d3d',
                  borderRadius: 3,
                  p: { xs: 4, md: 5 },
                  color: 'white',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <CalendarMonthIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.7)' }} />
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 700, color: 'white' }}>
                    {tr(c.book_appointment, lang)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
                  {`${tr(c.consultation_for, lang)}: ${service[titleKey]}`}
                </Typography>

                {submitted ? (
                  <Alert
                    severity="success"
                    sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '& .MuiAlert-icon': { color: '#6ee7b7' } }}
                  >
                    {t('contact.success')}
                  </Alert>
                ) : (
                  <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      { name: 'name', label: t('contact.name'), type: 'text', required: true },
                      { name: 'email', label: t('contact.email'), type: 'email', required: true },
                      { name: 'phone', label: t('contact.phone'), type: 'tel', required: false },
                      { name: 'company', label: t('contact.company'), type: 'text', required: false },
                    ].map((field) => (
                      <TextField
                        key={field.name}
                        label={field.label}
                        name={field.name}
                        type={field.type}
                        value={form[field.name as keyof AppointmentForm]}
                        onChange={handleChange}
                        required={field.required}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                            '&.Mui-focused fieldset': { borderColor: 'white' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                        }}
                      />
                    ))}

                    <TextField
                      label={tr(c.preferred_date, lang)}
                      name="preferred_date"
                      type="date"
                      value={form.preferred_date}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          colorScheme: 'dark',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'white' },
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                      }}
                    />

                    <TextField
                      label={t('contact.message')}
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'white' },
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                      }}
                    />

                    {submitError && (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={submitting}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        mt: 1,
                        bgcolor: 'white',
                        color: '#3d3d3d',
                        py: 1.5,
                        fontSize: '0.95rem',
                        '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-1px)' },
                        '&:disabled': { bgcolor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.5)' },
                      }}
                    >
                      {submitting
                        ? t('common.loading')
                        : tr(c.send_request, lang)}
                    </Button>
                  </Box>
                )}
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ServiceDetailPage;
